import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  ClickAwayListener,
  Container,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { DateRangePicker } from 'date-range-picker-mui';
import dayjs from 'dayjs';
import { CalendarRange, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportCSVComponent from 'src/components/ExportCSVComponent';
import Page from 'src/components/Page';
import CalculationBreakdownDialog from 'src/components/CalculationBreakdownDialog';
import { websiteTitle } from 'src/config';
import useGetGrantedSubAccounts from 'src/hooks/useGetGrantedSubAccounts';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { makeStyles } from 'tss-react/mui';
import AuditReportTable from './AuditReportTable';
import AuditReportSummary from './AuditReportSummary';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ALL_DATES,
  CUSTOM_DATE,
  MONTH_TO_DATE,
  SEVEN_DAYS,
  THIRTY_DAYS,
  THREE_HUNDRED_SIXTY_FIVE_DAYS,
  TODAYS_DATE,
  YESTERDAYS_DATE
} from 'src/constants';

const useStyles = makeStyles()(theme => ({
  metalRoot: {
    [theme.breakpoints.up('md')]: {
      padding: `0 ${theme.spacing(4)} ${theme.spacing(0.5)}`
    }
  },
  dateDropdown: {
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'var(--color-surface-raised)'
    }
  },
  // Positioning context for the date-range popup so it anchors to the trigger
  // (see datePicker's position: absolute) instead of hardcoded viewport coords.
  // The library wraps the popup in its own intermediate position:relative Box;
  // reset that direct child to static so the popup resolves against this row.
  dateDropdownRow: {
    position: 'relative',
    '& > .MuiBox-root': {
      position: 'static'
    }
  },
  datePicker: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 1001,
    border: '1px solid var(--Colors-Border-border-secondary, #242428)',
    borderRadius: 'var(--radius-xl, 12px)',
    '& .MuiBox-root': {
      height: 'auto !important',
      width: 'auto !important'
    },
    '& .MuiPaper-root': {
      backgroundColor: 'var(--neutral-2)',
      borderRadius: 'var(--radius-xl, 12px)'
    },
    '& .css-1g5dopa, .css-1efd2au, .css-m77go1': {
      backgroundColor: 'transparent'
    },
    '& .MuiTypography-root.MuiTypography-subtitle1': {
      fontSize: 16
    },
    '& .css-1efd2au Button, .css-m77go1 Button, .css-15lo3ef Button': {
      background: 'var(--Colors-Background-bg-brand-solid, #00D474) !important'
    },
    '& .MuiListItemButton-root': {
      color: 'var(--colors-text-text-secondary-700, #B2B2B8)'
    },
    '& .MuiButtonBase-root.MuiListItemButton-root': {
      color: 'var(--Colors-Text-text-secondary_hover, #EAECF0)',
      background: 'var(--neutral-2)',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.04)'
      }
    }
  },
  subAccountDropdown: {
    '& .MuiOutlinedInput-root': {
      padding: '1px 4px !important'
    },
    '& .MuiChip-root': {
      height: 30
    }
  },
  calculationInfo: {
    cursor: 'pointer',
    color: 'rgb(96, 165, 250)',
    '&:hover': {
      color: 'rgb(147 197 253 / var(--tw-text-opacity, 1));'
    }
  },
  mobileDateDropdown: {
    backgroundColor: 'var(--neutral-2)',
    border: '1px solid var(--color-border)',
    paddingTop: 0,
    paddingBottom: 0
  }
}));

const AuditReportView = props => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const pageTitle = websiteTitle + ' - Audit Report';
  const mediumDevice = useMediaQuery(theme.breakpoints.down('lg'));
  const smallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedGroup } = useSelectedGroup();
  const [includeArchived, setIncludeArchived] = useState(false);
  const {
    data: subAccounts,
    isLoading: subAccountsLoading
  } = useGetGrantedSubAccounts(selectedGroup.map(g => g.group.name), includeArchived);

  const [selectedSubAccounts, setSelectedSubAccounts] = useState([
    'All Sub-Accounts'
  ]);

  useEffect(() => {
    // Skip while the granted-subaccounts list is empty/in-flight: toggling
    // includeArchived changes the SWR key, so subAccounts is transiently []
    // and pruning against it would wipe a valid selection to 'All'. Once the
    // new list resolves this re-runs and prunes correctly.
    if (subAccountsLoading || !subAccounts.length) return;
    if (!selectedSubAccounts.includes('All Sub-Accounts')) {
      const validNames = subAccounts.map(sa => sa.name);
      const filtered = selectedSubAccounts.filter(name => validNames.includes(name));
      if (filtered.length !== selectedSubAccounts.length) {
        setSelectedSubAccounts(filtered.length ? filtered : ['All Sub-Accounts']);
      }
    }
  }, [subAccounts]);
  const [startDate, setStartDate] = useState(
    dayjs(
      dayjs()
        .subtract(30, 'days')
        .startOf('day')
        .toDate()
    ).format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    dayjs(dayjs().toDate()).format('YYYY-MM-DD')
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const dateRanges = [
    {
      label: 'Today',
      startDate: dayjs()
        .startOf('day')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Yesterday',
      startDate: dayjs()
        .subtract(1, 'day')
        .startOf('day')
        .toDate(),
      endDate: dayjs()
        .subtract(1, 'day')
        .endOf('day')
        .toDate()
    },
    {
      label: 'Last 7 Days',
      startDate: dayjs()
        .subtract(7, 'days')
        .startOf('day')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Month to Date',
      startDate: dayjs()
        .startOf('month')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Last 30 Days',
      startDate: dayjs()
        .subtract(30, 'days')
        .startOf('day')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Last 90 Days',
      startDate: dayjs()
        .subtract(90, 'days')
        .startOf('day')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Year to Date',
      startDate: dayjs()
        .startOf('year')
        .toDate(),
      endDate: dayjs().toDate()
    },
    {
      label: 'Last 365 Days',
      startDate: dayjs()
        .subtract(365, 'days')
        .startOf('day')
        .toDate(),
      endDate: dayjs().toDate()
    }
  ];
  const [formattedDateRange, setFormattedDateRange] = useState('Last 30 Days');
  const [tableExportData, setTableExportData] = useState([]);
  const [summaryStatsExport, setSummaryStatsExport] = useState([]);
  const [summaryWalletsExport, setSummaryWalletExport] = useState([]);
  const [methodologyDialogOpen, setMethodologyDialogOpen] = useState(false);
  const [showMinDateInfo, setShowMinDateInfo] = useState(false);
  const [range, setRange] = useState(THIRTY_DAYS);
  const [customSelected, setCustomSelected] = useState(false);
  const MIN_GET_BLOCK_COUNTS_DATE = '07/01/2020';

  const handleToggleDatePicker = event => {
    if (anchorEl == null) {
      setAnchorEl(event.currentTarget);
      setDatePickerOpen(true);
    } else {
      setAnchorEl(null);
      setDatePickerOpen(false);
    }
  };

  const handleDateChange = event => {
    setFormattedDateRange(formatDateRange(event.startDate, event.endDate));
    setStartDate(dayjs(event.startDate).format('YYYY-MM-DD'));
    setEndDate(dayjs(event.endDate).format('YYYY-MM-DD'));
    setAnchorEl(null);
    setDatePickerOpen(false);
    resetExportCache();
  };

  const handleSubAccountChange = (event, accounts) => {
    if (
      !selectedSubAccounts.includes('All Sub-Accounts') &&
      accounts.includes('All Sub-Accounts')
    ) {
      setSelectedSubAccounts(['All Sub-Accounts']);
    } else if (
      !accounts.length ||
      (selectedSubAccounts.includes('All Sub-Accounts') &&
        accounts.includes('All Sub-Accounts'))
    ) {
      setSelectedSubAccounts([]);
    } else if (!!accounts.length) {
      setSelectedSubAccounts([
        ...accounts.filter(acct => acct !== 'All Sub-Accounts')
      ]);
    }
    resetExportCache();
  };

  const formatDateRange = (startDate, endDate) => {
    const datesMatchExistingDateRange = dateRanges.filter(
      range =>
        dayjs(range.startDate)
          .toDate()
          .getTime() ===
          dayjs(startDate)
            .toDate()
            .getTime() &&
        dayjs(range.endDate)
          .toDate()
          .getTime() ===
          dayjs(endDate)
            .toDate()
            .getTime()
    );
    if (datesMatchExistingDateRange.length > 0) {
      return datesMatchExistingDateRange[0].label;
    } else if (
      !datesMatchExistingDateRange.length &&
      dayjs(startDate).get('year') === dayjs(endDate).get('year')
    ) {
      return `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format(
        'MMM DD, YYYY'
      )}`;
    } else {
      return `${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(
        endDate
      ).format('MMM DD, YYYY')}`;
    }
  };

  const getExportDataAndFileInfo = () => {
    return [
      { fileName: 'Audit Report - Daily Stats - ', data: tableExportData },
      { fileName: 'Audit Report - Summary - ', data: summaryStatsExport },
      { fileName: 'Audit Report - Wallets - ', data: summaryWalletsExport }
    ];
  };

  const handleStartDateChange = event => {
    let selectedDate = dayjs(event).format('YYYY-MM-DD');
    setStartDate(selectedDate);
    if (selectedDate > endDate) {
      setEndDate(selectedDate);
    }
    resetExportCache();
  };

  const handleEndDateChange = event => {
    let selectedDate = dayjs(event).format('YYYY-MM-DD');
    setEndDate(selectedDate);
    if (selectedDate < startDate) {
      setStartDate(selectedDate);
    }
    resetExportCache();
  };

  const handleRangeChange = event => {
    resetExportCache();
    let timeRange = event.target.value;
    timeRange = Number(timeRange);
    setRange(timeRange);
    if (timeRange === -1) {
      setCustomSelected(true);
    } else {
      setCustomSelected(false);
      // all dates since min block date
      if (timeRange === 0) {
        setStartDate(dayjs(MIN_GET_BLOCK_COUNTS_DATE).format('YYYY-MM-DD'));
        setEndDate(dayjs().format('YYYY-MM-DD'));
      }
      // today
      else if (timeRange === -2) {
        setStartDate(dayjs().format('YYYY-MM-DD'));
        setEndDate(dayjs().format('YYYY-MM-DD'));
        // yesterday
      } else if (timeRange === -3) {
        setStartDate(
          dayjs()
            .subtract(1, 'day')
            .format('YYYY-MM-DD')
        );
        setEndDate(
          dayjs()
            .subtract(1, 'day')
            .format('YYYY-MM-DD')
        );
        // month to date
      } else if (timeRange === -4) {
        setCustomSelected(false);
        setStartDate(
          dayjs()
            .date(1)
            .format('YYYY-MM-DD')
        );
        setEndDate(dayjs().format('YYYY-MM-DD'));
      } else {
        // handle all multiple preset date ranges other than 'month to date' and 'all dates'
        setStartDate(
          dayjs(new Date().getTime() - timeRange).format('YYYY-MM-DD')
        );
        setEndDate(dayjs().format('YYYY-MM-DD'));
      }
    }
  };

  const resetExportCache = () => {
    setTableExportData([]);
    setSummaryStatsExport([]);
    setSummaryWalletExport([]);
  };

  return (
    <Page className={classes.metalRoot} title={pageTitle}>
      <Container
        maxWidth="xxl"
        style={mediumDevice ? { padding: '0 8px' } : {}}
      >
        <Box sx={{ backgroundColor: 'var(--neutral-2)', borderRadius: '4px' }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            p={2}
          >
            <Typography
              sx={{
                color: 'var(--color-text)',
                padding: 0
              }}
            >
              Learn more about the data in the table below{' '}
              <span
                className={classes.calculationInfo}
                onClick={() => setMethodologyDialogOpen(true)}
                data-testid="fdr_lnk_trust_center_audit_here"
              >
                here
              </span>
              .
            </Typography>
            {mediumDevice ? (
              <Box py={2}>
                <Grid container sx={{ width: '100%' }} rowSpacing={1.5}>
                  <Grid
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}
                  >
                    <FormControl variant="standard" style={{ minWidth: 90 }}>
                      <InputLabel>{t('timeRange')}</InputLabel>
                      <Select
                        variant="standard"
                        value={range}
                        onChange={handleRangeChange}
                        MenuProps={{
                          PopoverClasses: {
                            paper: classes.mobileDateDropdown
                          }
                        }}
                        sx={{
                          ':after': { borderBottomColor: 'var(--accent-11)' }
                        }}
                      >
                        <MenuItem value={TODAYS_DATE}>Today</MenuItem>
                        <MenuItem value={YESTERDAYS_DATE}>Yesterday</MenuItem>
                        <MenuItem value={SEVEN_DAYS}>
                          {t('lastSevenDays')}
                        </MenuItem>
                        <MenuItem value={THIRTY_DAYS}>
                          {t('lastThirtyDays')}
                        </MenuItem>
                        <MenuItem value={MONTH_TO_DATE}>
                          {t('monthToDate')}
                        </MenuItem>
                        <MenuItem value={THREE_HUNDRED_SIXTY_FIVE_DAYS}>
                          {t('lastThreeHundredSixtyFiveDays')}
                        </MenuItem>
                        <MenuItem value={ALL_DATES}>{t('all')}</MenuItem>
                        <MenuItem value={CUSTOM_DATE}>{t('custom')}</MenuItem>
                      </Select>
                    </FormControl>
                    <ClickAwayListener
                      onClickAway={() => setShowMinDateInfo(false)}
                    >
                      <Tooltip
                        onClick={() => setShowMinDateInfo(!showMinDateInfo)}
                        onClose={() => setShowMinDateInfo(false)}
                        open={showMinDateInfo}
                        disableFocusListener
                        disableHoverListener
                        title={
                          <Typography
                            color="var(--color-text)"
                            variant="h6"
                            pt={1}
                            pl={2}
                            pb={0.5}
                          >
                            {t('oneYearTransactionsDataOnly')}
                          </Typography>
                        }
                        arrow
                      >
                        <Info
                          size={20}
                          style={{
                            marginLeft: theme.spacing(1),
                            marginRight: theme.spacing(3),
                            marginTop: theme.spacing(2),
                            cursor: 'pointer'
                          }}
                          color="var(--color-text)"
                        />
                      </Tooltip>
                    </ClickAwayListener>
                  </Grid>
                  <Grid sx={{ width: '100%' }}>
                    <DatePicker
                      label={t('startDate')}
                      name="start"
                      format="YYYY-MM-DD"
                      disableFuture
                      style={{ width: theme.spacing(20) }}
                      onChange={handleStartDateChange}
                      value={dayjs(startDate)}
                      disabled={!customSelected}
                      maxDate={dayjs(endDate)}
                      minDate={dayjs(MIN_GET_BLOCK_COUNTS_DATE)}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          sx: {
                            '& .MuiInput-root.Mui-disabled::before': {
                              borderBottomStyle: 'solid',
                              borderBottomColor: 'rgba(255, 255, 255, 0.5)'
                            }
                          }
                        },
                        toolbar: {
                          sx: {
                            '& .MuiDateTimePickerToolbar-timeContainer': {
                              alignItems: 'center'
                            }
                          }
                        },
                        desktopPaper: {
                          style: {
                            backgroundColor: 'var(--neutral-2)',
                            border: '1px solid var(--color-border)'
                          },
                          sx: {
                            '& .MuiPickersDay-root': {
                              backgroundColor: 'var(--neutral-2)'
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                              backgroundColor: 'var(--color-menu-selected)'
                            }
                          }
                        },
                        mobilePaper: {
                          style: {
                            backgroundColor: 'var(--neutral-2)',
                            border: '1px solid var(--color-border)'
                          },
                          sx: {
                            '& .MuiPickersDay-root': {
                              backgroundColor: 'var(--neutral-2)'
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                              backgroundColor: 'var(--color-menu-selected)'
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid sx={{ width: '100%' }}>
                    <DatePicker
                      label={t('endDate')}
                      name="end"
                      format="YYYY-MM-DD"
                      disableFuture
                      style={{ width: theme.spacing(20) }}
                      onChange={handleEndDateChange}
                      value={dayjs(endDate)}
                      disabled={!customSelected}
                      minDate={dayjs(startDate)}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          sx: {
                            '& .MuiInput-root.Mui-disabled::before': {
                              borderBottomStyle: 'solid',
                              borderBottomColor: 'rgba(255, 255, 255, 0.5)'
                            }
                          }
                        },
                        toolbar: {
                          sx: {
                            '& .MuiDateTimePickerToolbar-timeContainer': {
                              alignItems: 'center'
                            }
                          }
                        },
                        desktopPaper: {
                          style: {
                            backgroundColor: 'var(--neutral-2)',
                            border: '1px solid var(--color-border)'
                          },
                          sx: {
                            '& .MuiPickersDay-root': {
                              backgroundColor: 'var(--neutral-2)'
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                              backgroundColor: 'var(--color-menu-selected)'
                            }
                          }
                        },
                        mobilePaper: {
                          style: {
                            backgroundColor: 'var(--neutral-2)',
                            border: '1px solid var(--color-border)'
                          },
                          sx: {
                            '& .MuiPickersDay-root': {
                              backgroundColor: 'var(--neutral-2)'
                            },
                            '& .MuiPickersDay-root.Mui-selected': {
                              backgroundColor: 'var(--color-menu-selected)'
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid sx={{ width: '100%', marginTop: theme.spacing(1.5) }}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Autocomplete
                        multiple={true}
                        disableCloseOnSelect
                        loading={subAccountsLoading}
                        disabled={subAccountsLoading}
                        sx={{
                          border:
                            'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)'
                        }}
                        getOptionDisabled={option =>
                          selectedSubAccounts.includes('All Sub-Accounts') &&
                          option !== 'All Sub-Accounts'
                        }
                        onChange={handleSubAccountChange}
                        options={['All Sub-Accounts', ...subAccounts.map(acct => acct.name).sort()]}
                        defaultValue={['All Sub-Accounts']}
                        value={selectedSubAccounts}
                        popupIcon={
                          <ChevronDown
                            style={{
                              color:
                                'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                            }}
                          />
                        }
                        slotProps={{
                          popper: {
                            style: {
                              width: 400
                            },
                            placement: 'bottom-start'
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            style={{
                              width: smallDevice ? 220 : 250,
                              background: 'var(--neutral-2)'
                            }}
                            variant="outlined"
                            className={classes.subAccountDropdown}
                            {...params}
                            placeholder={
                              !selectedSubAccounts.length
                                ? 'Select Sub-Accounts'
                                : ''
                            }
                          />
                        )}
                        renderOption={(props, option, { selected }) => {
                          return (
                            <li key={option.key} {...props}>
                              <Checkbox
                                style={{
                                  marginRight: 8,
                                  paddingTop: 5,
                                  paddingBottom: 5
                                }}
                                checked={
                                  selected ||
                                  (selectedSubAccounts.length &&
                                    selectedSubAccounts.includes(
                                      'All Sub-Accounts'
                                    ))
                                }
                                disabled={
                                  selectedSubAccounts.length &&
                                  selectedSubAccounts.includes(
                                    'All Sub-Accounts'
                                  ) &&
                                  option !== 'All Sub-Accounts'
                                }
                              />
                              <Typography>{option}</Typography>
                            </li>
                          );
                        }}
                        renderTags={(value, getTagProps) => {
                          if (value[0] === 'All Sub-Accounts') {
                            return (
                              <Chip
                                style={{
                                  border: '1px solid #364152',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color:
                                    'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                                }}
                                label={'All Sub-Accounts'}
                              />
                            );
                          } else {
                            return (
                              <Chip
                                style={{
                                  border: '1px solid #364152',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color:
                                    'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                                }}
                                label={`${selectedSubAccounts.length} ${
                                  value.length === 1
                                    ? 'Sub-Account'
                                    : 'Sub-Accounts'
                                }`}
                              />
                            );
                          }
                        }}
                      />
                      <ExportCSVComponent
                        fileName={`Audit Report - `}
                        variant={'contained'}
                        disableExport={
                          !selectedSubAccounts.length ||
                          !tableExportData.length ||
                          !summaryStatsExport.length ||
                          !summaryWalletsExport.length
                        }
                        groupReport={true}
                        getCSVData={getExportDataAndFileInfo}
                        singleDate={
                          dayjs(startDate).toDate() === dayjs(endDate).toDate()
                        }
                        startDate={dayjs(startDate).format('YYYY-MM-DD')}
                        endDate={dayjs(endDate).format('YYYY-MM-DD')}
                        pdfOption={false}
                        multiFileExport={true}
                      />
                    </Box>
                  </Grid>
                  <Grid sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                    <FormGroup>
                      <FormControlLabel
                        labelPlacement={'end'}
                        control={
                          <Checkbox
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            disabled={subAccountsLoading}
                          />
                        }
                        label="Include Archived"
                        sx={{
                          whiteSpace: 'nowrap',
                          color: 'rgb(128,128,128)',
                          '& .MuiTypography-root': {
                            color: 'rgb(230, 229, 232)'
                          },
                          '& .Mui-checked': {
                            color: 'var(--color-menu-selected)'
                          }
                        }}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box className={classes.dateDropdownRow} display="flex" flexDirection="row" alignItems="center">
                <Button
                  variant="outlined"
                  onClick={handleToggleDatePicker}
                  style={{
                    height: 40,
                    color:
                      'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)',
                    border: datePickerOpen
                      ? '1px solid var(--accent-9)'
                      : '1px solid var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)',
                    zIndex: 1000,
                    textTransform: 'capitalize',
                    background: 'var(--neutral-2)',
                    whiteSpace: 'nowrap'
                  }}
                  className={classes.dateDropdown}
                  startIcon={
                    <CalendarRange size={23} style={{ marginBottom: 2 }} />
                  }
                  endIcon={datePickerOpen ? <ChevronUp /> : <ChevronDown />}
                >
                  {formattedDateRange}
                </Button>
                <DateRangePicker
                  open={datePickerOpen}
                  onChange={handleDateChange}
                  toggle={handleToggleDatePicker}
                  initialDateRange={{ startDate, endDate }}
                  minDate={
                    new Date(
                      Math.max(
                        dayjs(endDate)
                          .subtract(365, 'days')
                          .valueOf(),
                        dayjs(MIN_GET_BLOCK_COUNTS_DATE).valueOf()
                      )
                    )
                  }
                  maxDate={new Date()}
                  definedRanges={dateRanges}
                  wrapperClassName={classes.datePicker}
                />
                <ClickAwayListener
                  onClickAway={() => setShowMinDateInfo(false)}
                >
                  <Tooltip
                    onClick={() => setShowMinDateInfo(!showMinDateInfo)}
                    onClose={() => setShowMinDateInfo(false)}
                    open={showMinDateInfo}
                    disableFocusListener
                    disableHoverListener
                    title={
                      <Typography
                        color="var(--color-text)"
                        variant="h6"
                        pt={1}
                        pl={1.5}
                        pb={0.5}
                      >
                        {t('oneYearTransactionsDataOnly')}
                      </Typography>
                    }
                    arrow
                  >
                    <Info
                      size={20}
                      style={{
                        marginLeft: theme.spacing(1),
                        marginRight: theme.spacing(3),
                        cursor: 'pointer'
                      }}
                      color="var(--color-text)"
                    />
                  </Tooltip>
                </ClickAwayListener>
                <Autocomplete
                  multiple={true}
                  disableCloseOnSelect
                  loading={subAccountsLoading}
                  disabled={subAccountsLoading}
                  sx={{
                    marginRight: '16px',
                    border:
                      'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)'
                  }}
                  getOptionDisabled={option =>
                    selectedSubAccounts.includes('All Sub-Accounts') &&
                    option !== 'All Sub-Accounts'
                  }
                  onChange={handleSubAccountChange}
                  options={['All Sub-Accounts', ...subAccounts.map(acct => acct.name).sort()]}
                  defaultValue={['All Sub-Accounts']}
                  value={selectedSubAccounts}
                  popupIcon={
                    <ChevronDown
                      style={{
                        color:
                          'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                      }}
                      data-testid="fdr_drp_trust_center_sub_account_search"
                    />
                  }
                  slotProps={{
                    popper: {
                      style: {
                        width: 400
                      },
                      placement: 'bottom-start'
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      style={{
                        width: smallDevice ? 220 : 250,
                        background: 'var(--neutral-2)'
                      }}
                      variant="outlined"
                      className={classes.subAccountDropdown}
                      {...params}
                      placeholder={
                        !selectedSubAccounts.length ? 'Select Sub-Accounts' : ''
                      }
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li
                        key={key}
                        {...otherProps}
                        data-testid={`fdr_opt_trust_center_sub_account_${option
                          .replace(/\s+/g, '_')
                          .toLowerCase()}`}
                      >
                        <Checkbox
                          style={{
                            marginRight: 8,
                            paddingTop: 5,
                            paddingBottom: 5
                          }}
                          checked={
                            selected ||
                            (selectedSubAccounts.length &&
                              selectedSubAccounts.includes('All Sub-Accounts'))
                          }
                          disabled={
                            selectedSubAccounts.length &&
                            selectedSubAccounts.includes('All Sub-Accounts') &&
                            option !== 'All Sub-Accounts'
                          }
                        />
                        <Typography>{option}</Typography>
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) => {
                    if (value[0] === 'All Sub-Accounts') {
                      return (
                        <Chip
                          style={{
                            border: '1px solid #364152',
                            fontSize: '14px',
                            fontWeight: 600,
                            color:
                              'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                          }}
                          label={'All Sub-Accounts'}
                        />
                      );
                    } else {
                      return (
                        <Chip
                          style={{
                            border: '1px solid #364152',
                            fontSize: '14px',
                            fontWeight: 600,
                            color:
                              'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-fg, #CDD5DF)'
                          }}
                          label={`${selectedSubAccounts.length} ${
                            value.length === 1 ? 'Sub-Account' : 'Sub-Accounts'
                          }`}
                        />
                      );
                    }
                  }}
                />
                <FormGroup>
                  <FormControlLabel
                    labelPlacement={'end'}
                    control={
                      <Checkbox
                        checked={includeArchived}
                        onChange={(e) => setIncludeArchived(e.target.checked)}
                        disabled={subAccountsLoading}
                      />
                    }
                    label="Include Archived"
                    sx={{
                      whiteSpace: 'nowrap',
                      color: 'rgb(128,128,128)',
                      '& .MuiTypography-root': {
                        color: 'rgb(230, 229, 232)'
                      },
                      '& .Mui-checked': {
                        color: 'var(--color-menu-selected)'
                      }
                    }}
                  />
                </FormGroup>
                <ExportCSVComponent
                  fileName={`Audit Report - `}
                  variant={'contained'}
                  disableExport={
                    !selectedSubAccounts.length || !summaryStatsExport.length
                  }
                  groupReport={true}
                  getCSVData={getExportDataAndFileInfo}
                  singleDate={
                    dayjs(startDate).toDate() === dayjs(endDate).toDate()
                  }
                  startDate={dayjs(startDate).format('YYYY-MM-DD')}
                  endDate={dayjs(endDate).format('YYYY-MM-DD')}
                  pdfOption={false}
                  multiFileExport={true}
                />
              </Box>
            )}
          </Box>
          <Box>
            <AuditReportSummary
              startDate={startDate}
              endDate={endDate}
              subAccounts={
                selectedSubAccounts.includes('All Sub-Accounts')
                  ? subAccounts?.map(acct => acct.name)
                  : selectedSubAccounts
              }
              saveSummaryStatsExport={setSummaryStatsExport}
              saveSummaryWalletsExport={setSummaryWalletExport}
            />
            <AuditReportTable
              startDate={startDate}
              endDate={endDate}
              subAccounts={
                selectedSubAccounts.includes('All Sub-Accounts')
                  ? subAccounts
                  : subAccounts.filter(sa => selectedSubAccounts.includes(sa.name))
              }
              subAccountsLoading={subAccountsLoading}
              saveExportData={setTableExportData}
              includeArchived={includeArchived}
            />
          </Box>
        </Box>
      </Container>

      <CalculationBreakdownDialog
        open={methodologyDialogOpen}
        onClose={() => setMethodologyDialogOpen(false)}
      />
    </Page>
  );
};

export default AuditReportView;
